import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Palette } from 'lucide-react';

const DEFAULT_SETTINGS = {
  line1Color: '#C9A227',
  line2Color: '#3B82F6',
  barExpectedColor: '#e2e8f0',
  barCollectedColor: '#10b981',
  pieColors: '#3b82f6,#6366f1,#8b5cf6,#a855f7,#06b6d4,#14b8a6,#10b981,#f59e0b,#ef4444,#ec4899,#64748b,#84cc16',
  chartHeight: '220',
  funnelLeadColor: '#3b82f6',
  funnelOppColor: '#8b5cf6',
};

const STORAGE_KEY = 'crm_chart_settings';

export function getChartSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return { ...DEFAULT_SETTINGS };
}

export function getPieColors() {
  const s = getChartSettings();
  return s.pieColors.split(',').map(c => c.trim()).filter(Boolean);
}

export function ChartSettingsButton({ onSettingsChange }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(getChartSettings);

  const save = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (err) {
      // Quota exceeded / private mode / storage disabled — fall back to in-memory only
      // eslint-disable-next-line no-console
      console.warn('ChartSettings: unable to persist to localStorage, using session-only state', err);
    }
    if (onSettingsChange) onSettingsChange(settings);
    setOpen(false);
  };

  const reset = () => {
    setSettings({ ...DEFAULT_SETTINGS });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* no-op: ignoring localStorage unavailability */
    }
    if (onSettingsChange) onSettingsChange(DEFAULT_SETTINGS);
  };

  const update = (key, val) => setSettings(p => ({ ...p, [key]: val }));

  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(true)} title="Chart Settings" data-testid="chart-settings-btn">
        <Palette className="h-4 w-4 text-slate-400" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Palette className="h-5 w-5" /> Chart Appearance</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <Section title="Line Charts">
              <ColorRow label="Line 1 (Land Plot)" value={settings.line1Color} onChange={v => update('line1Color', v)} />
              <ColorRow label="Line 2 (Hotel Share)" value={settings.line2Color} onChange={v => update('line2Color', v)} />
            </Section>
            <Section title="Bar Charts">
              <ColorRow label="Expected" value={settings.barExpectedColor} onChange={v => update('barExpectedColor', v)} />
              <ColorRow label="Collected" value={settings.barCollectedColor} onChange={v => update('barCollectedColor', v)} />
            </Section>
            <Section title="Stage Funnel">
              <ColorRow label="Lead Stages" value={settings.funnelLeadColor} onChange={v => update('funnelLeadColor', v)} />
              <ColorRow label="Opportunity Stages" value={settings.funnelOppColor} onChange={v => update('funnelOppColor', v)} />
            </Section>
            <Section title="Chart Size">
              <div>
                <Label className="text-xs">Height (px)</Label>
                <Select value={settings.chartHeight} onValueChange={v => update('chartHeight', v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="180">Compact (180px)</SelectItem>
                    <SelectItem value="220">Default (220px)</SelectItem>
                    <SelectItem value="280">Large (280px)</SelectItem>
                    <SelectItem value="350">Extra Large (350px)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Section>
            <Section title="Pie Chart Colors">
              <p className="text-[10px] text-slate-400 mb-1">Comma-separated hex colors (order matters)</p>
              <Input className="h-8 text-xs font-mono" value={settings.pieColors} onChange={e => update('pieColors', e.target.value)} />
              <div className="flex gap-1 mt-1 flex-wrap">
                {settings.pieColors.split(',').map((c, i) => (
                  <div key={`${c.trim()}-${i}`} className="w-4 h-4 rounded border border-slate-200" style={{ backgroundColor: c.trim() }} />
                ))}
              </div>
            </Section>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={reset}>Reset Defaults</Button>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</h4>
      {children}
    </div>
  );
}

function ColorRow({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-xs flex-1">{label}</Label>
      <div className="flex items-center gap-1.5">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-7 h-7 rounded cursor-pointer border border-slate-200" />
        <Input className="w-20 h-7 text-xs font-mono" value={value} onChange={e => onChange(e.target.value)} />
      </div>
    </div>
  );
}
